import { isbot } from 'isbot';
import { get } from 'lodash';

// ----------------------------------------------------------------------
// TYPES
// ----------------------------------------------------------------------

interface EmailClientDetectionResult {
  isEmailClient: boolean;
  confidence: number;
  detectedSignals: string[];
}

// ----------------------------------------------------------------------
// EMAIL CLIENT DETECTION
// ----------------------------------------------------------------------

// NOTE: These patterns were sourced with help from LLMs (Claude Opus / GPT)
// and cross-checked against the user-agents.net email-clients database
// (https://user-agents.net/email-clients).

// Not all patterns could be cross-checked as UA strings for many email
// clients and scanners are not publicly documented.

// Known email client User-Agent patterns.
const EMAIL_CLIENT_UA_PATTERNS = [
  { pattern: /Outlook/i, name: 'outlook' },
  { pattern: /OWA/i, name: 'outlook-web-access' },
  { pattern: /AppleMail/i, name: 'apple-mail' },
  { pattern: /Thunderbird/i, name: 'thunderbird' },
  { pattern: /The Bat/i, name: 'the-bat' },
  { pattern: /Yahoo.*Mail/i, name: 'yahoo-mail' },
  { pattern: /Lotus-Notes/i, name: 'lotus-notes' },
  { pattern: /Airmail/i, name: 'airmail' },
  { pattern: /Barca/i, name: 'barca' },
  { pattern: /Eudora/i, name: 'eudora' },
  { pattern: /Evolution/i, name: 'evolution' },
  { pattern: /kmail2/i, name: 'kmail2' },
  { pattern: /kontact/i, name: 'kontact' },
  { pattern: /MailMaster/i, name: 'mail-master' },
  { pattern: /mobmail/i, name: 'mobmail' },
  { pattern: /Postbox/i, name: 'postbox' },
  { pattern: /Sparrow/i, name: 'sparrow' },
  { pattern: /zdesktop/i, name: 'zimbra-desktop' }
];

// Known link-preview / security-scanner bot User-Agent patterns.
const LINK_PREVIEW_UA_PATTERNS = [
  { pattern: /GoogleImageProxy/i, name: 'google-image-proxy' },
  { pattern: /Barracuda/i, name: 'barracuda' },
  { pattern: /Symantec/i, name: 'symantec' },
  { pattern: /Mimecast/i, name: 'mimecast' },
  { pattern: /Proofpoint/i, name: 'proofpoint' },
  { pattern: /ZScaler/i, name: 'zscaler' },
  { pattern: /FireEye/i, name: 'fireeye' },
  { pattern: /MessageLabs/i, name: 'messagelabs' }
];

export function detectEmailClient(): EmailClientDetectionResult {
  const detectedSignals: string[] = [];
  let score = 0;
  const maxScore = 10;

  const userAgent = navigator.userAgent;

  // Known email client UA match

  const matchedClient = EMAIL_CLIENT_UA_PATTERNS.find(({ pattern }) => pattern.test(userAgent));
  if (matchedClient) {
    score += 4;
    detectedSignals.push(`ua-match:${matchedClient.name}`);
  }

  // Known link-preview/security-scanner bot UA match

  const matchedBot = LINK_PREVIEW_UA_PATTERNS.find(({ pattern }) => pattern.test(userAgent));
  if (matchedBot) {
    score += 4;
    detectedSignals.push(`ua-match:${matchedBot.name}`);
  }

  // `isbot` covers a broad range of crawlers, scrapers, and link-preview bots
  // beyond our LLM-generated lists above.

  if (!matchedClient && !matchedBot && isbot(userAgent)) {
    score += 4;
    detectedSignals.push('isbot-detected');
  }

  // WebDriver flag: Automated environments (e.g. headless browsers used
  // by link scanners) may set this property. Strong signal of non-human traffic.
  // https://developer.mozilla.org/en-US/docs/Web/API/Navigator/webdriver

  if (navigator.webdriver) {
    score += 4;
    detectedSignals.push('webdriver-detected');
  }

  // Page Visibility: Email clients and link scanners may render pages
  // in a hidden state as they don't display content to the user.
  // https://developer.mozilla.org/en-US/docs/Web/API/Page_Visibility_API

  if (document.hidden || document.visibilityState === 'hidden') {
    score += 2;
    detectedSignals.push('page-hidden');
  }

  // Zero-dimension viewport: Preview panes and headless renderers
  // may use tiny or zero-size viewports.
  // https://developer.mozilla.org/en-US/docs/Web/API/Window/innerWidth

  if (window.innerWidth === 0 || window.innerHeight === 0) {
    score += 2;
    detectedSignals.push('zero-viewport');
  }

  // Missing Service Worker support: Email clients and link scanners
  // typically use stripped-down rendering engines without full PWA capabilities.
  // https://developer.mozilla.org/en-US/docs/Web/API/ServiceWorker

  if (!('serviceWorker' in navigator)) {
    score += 1;
    detectedSignals.push('no-service-worker');
  }

  // Missing localStorage: Email clients and link scanners
  // typically use stripped-down rendering engines without full storage capabilities.
  // https://developer.mozilla.org/en-US/docs/Web/API/Window/localStorage

  if (!('localStorage' in window)) {
    score += 1;
    detectedSignals.push('no-local-storage');
  }

  const confidence = Math.min(score / maxScore, 1);
  const isEmailClient = score >= 4;

  return {
    isEmailClient,
    confidence: Math.round(confidence * 100) / 100,
    detectedSignals
  };
}

// Events that prove a real human is interacting with the page.
// Used to gate auto-save until genuine user activity is detected.

export const INTERACTION_EVENTS = [
  'click',
  'keydown',
  'scroll',
  'touchstart',
  'mousemove',
  'pointerdown',
  'focusin'
] as const;

// ----------------------------------------------------------------------
// SAFARI MOBILE DETECTION
// ----------------------------------------------------------------------

export const isSafariMobile = () => {
  const userAgent = get(window, ['navigator', 'userAgent'], null);

  if (!userAgent) {
    return;
  }

  const iOS = !!userAgent.match(/iPad/i) || !!userAgent.match(/iPhone/i);
  const webkit = !!userAgent.match(/WebKit/i);
  return iOS && webkit && !userAgent.match(/CriOS/i);
};

export const getIOSVersion = () => {
  const userAgent = get(window, ['navigator', 'userAgent'], null);

  if (!userAgent) {
    return null;
  }

  const iOSVersion = userAgent.match(/OS (\d+)_(\d+)/) || [];

  if (iOSVersion) {
    const iOSMajor = iOSVersion[1];
    const iOSMinor = iOSVersion[2];
    return { iOSMajor, iOSMinor };
  }

  return null;
};
